import { camelCase, mapKeys, orderBy, uniqBy } from 'lodash';
import { useQuery } from 'react-query';

import {
  ProjectTemplate,
  ProjectTemplateSummary
} from '@experiment-management-shared/types';
import { ProjectViewRow, projectViewsStore } from '@shared';
import api, { QueryConfig } from '@shared/api';
import { useGenerateUnsavedViewId } from './useGenerateUnsavedViewTemplateId';
import useProject from './useProject';

const VIEW_VERSION = 3;

const getLocalViews = async ({ projectId }: { projectId: string }) => {
  const rows = await projectViewsStore
    .where({ projectId })
    .toArray()
    .catch(e => {
      console.warn('DB CONNECTION ERROR', e);
      return [] as ProjectViewRow[];
    });

  return rows.map(row => row.view);
};

const filterAndCombineViews = async (
  serverViews: ProjectTemplateSummary[],
  localViews: ProjectTemplate[],
  unsavedTemplateId: string | null
) => {
  const matchedLocalViews = localViews.filter(localView => {
    return (
      (unsavedTemplateId && localView.templateId === unsavedTemplateId) ||
      serverViews.some(
        serverView => serverView.templateId === localView.templateId
      )
    );
  });

  // Return only latest version (local or server)
  return uniqBy(
    orderBy(
      [...serverViews, ...matchedLocalViews],
      [view => view.lastUpdate || 0],
      'desc'
    ),
    'templateId'
  );
};

const getProjectViews = async ({
  projectId,
  signal,
  unsavedTemplateId
}: {
  projectId: string;
  signal?: AbortSignal;
  unsavedTemplateId: string | null;
}) => {
  const { data: serverViews } = await api.get<ProjectTemplateSummary[]>(
    'dashboard-templates/project/get-all-summary',
    {
      params: { projectId, version: VIEW_VERSION },
      signal
    }
  );
  const normalizedServerViews = serverViews.map(view => {
    return mapKeys(view, (_, key) => camelCase(key)) as ProjectTemplateSummary;
  });
  const localViews = await getLocalViews({ projectId });

  return filterAndCombineViews(
    normalizedServerViews,
    localViews,
    unsavedTemplateId
  );
};

export default function useProjectViews<SelectData = ProjectTemplateSummary[]>(
  config: QueryConfig<ProjectTemplateSummary[], SelectData> = {}
) {
  const { data: project } = useProject();
  const projectId = project?.projectId;
  const configEnabled = config?.enabled ?? true;
  const unsavedTemplateId = useGenerateUnsavedViewId();

  return useQuery(
    ['projectViews', { projectId }],
    ({ signal }) => {
      return getProjectViews({ projectId, signal, unsavedTemplateId });
    },
    {
      ...config,
      enabled: configEnabled && !!projectId
    }
  );
}
